import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

Chart.register(...registerables);

interface Props {
  location: string;
}

type Row = {
  location_name: string;
  geography_type: string;
  year: number;
  cash_public_assistance_or_snap: string;
  percent: number | null;
};

type SeriesMap = Record<string, Record<number, number>>;

export const CashPublicAssistanceChart: React.FC<Props> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const compareCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const compareChartRef = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [activeChart, setActiveChart] = useState<'snapshot' | 'comparison'>('snapshot');

  useEffect(() => {
    const load = async () => {
      if (!location) {
        setLoading(false);
        setHasData(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: localData, error: localErr } = await supabaseClient
          .from('cash_public_assistance')
          .select('location_name, geography_type, year, cash_public_assistance_or_snap, percent')
          .ilike('location_name', location)
          .order('year', { ascending: true });

        if (localErr) throw localErr;

        const { data: caData, error: caErr } = await supabaseClient
          .from('cash_public_assistance')
          .select('location_name, geography_type, year, cash_public_assistance_or_snap, percent')
          .or('location_name.ilike.california,location_name.ilike.state of california')
          .order('year', { ascending: true });

        if (caErr) throw caErr;

        const cleaned = ([...(localData || []), ...(caData || [])] || []).filter(
          (r: any) => r.year != null && r.cash_public_assistance_or_snap && r.percent != null
        ) as Row[];

        if (!cleaned.length) {
          setHasData(false);
          setLoading(false);
          return;
        }

        setRows(cleaned);
        setHasData(true);
        setLoading(false);
      } catch (e: any) {
        console.error('CashPublicAssistanceChart: error loading data', e);
        setError(e?.message || 'Failed to load cash public assistance data.');
        setHasData(false);
        setLoading(false);
      }
    };

    load();
  }, [location]);

  const {
    years,
    seriesByCategory,
    percentWithAnyByYear,
    caPercentWithAnyByYear,
    latestYear,
    latestPercentWithAny,
    latestCaliforniaPercentWithAny
  } = useMemo(() => {
    if (!rows.length) {
      return {
        years: [] as number[],
        seriesByCategory: {} as SeriesMap,
        percentWithAnyByYear: {} as Record<number, number>,
        caPercentWithAnyByYear: {} as Record<number, number>,
        latestYear: null as number | null,
        latestPercentWithAny: null as number | null,
        latestCaliforniaPercentWithAny: null as number | null
      };
    }

    const yearsSet = new Set<number>();
    const series: SeriesMap = {};
    const normalizePercent = (raw: number): number => {
      // Supabase table stores proportions (0-1); convert to percent scale (0-100).
      // If values are already on 0-100 scale, keep them unchanged.
      return raw <= 1 ? raw * 100 : raw;
    };

    const isCalifornia = (name: string) => {
      const lc = name.toLowerCase().trim();
      return lc === 'california' || lc === 'state of california';
    };

    const localRows = rows.filter(r => !isCalifornia(String(r.location_name || '')));
    const californiaRows = rows.filter(r => isCalifornia(String(r.location_name || '')));

    localRows.forEach(r => {
      const year = Number(r.year);
      const cat = String(r.cash_public_assistance_or_snap);
      const rawPct = Number(r.percent ?? 0);
      const pct = normalizePercent(rawPct);
      if (!Number.isFinite(year) || !Number.isFinite(pct)) return;
      yearsSet.add(year);
      if (!series[cat]) series[cat] = {};
      series[cat][year] = pct;
    });

    const yearsArr = Array.from(yearsSet).sort((a, b) => a - b);

    // Build "with assistance/SNAP" explicitly from category labels for local area
    const percentAny: Record<number, number> = {};
    yearsArr.forEach(year => {
      let total = 0;
      Object.entries(series).forEach(([cat, map]) => {
        const v = map[year];
        const lc = cat.toLowerCase();
        const isAssisted = lc.startsWith('with');
        if (isAssisted && Number.isFinite(v)) {
          total += v;
        }
      });
      percentAny[year] = total;
    });

    // Build California "with assistance/SNAP" for comparison
    const caSeries: SeriesMap = {};
    californiaRows.forEach(r => {
      const year = Number(r.year);
      const cat = String(r.cash_public_assistance_or_snap);
      const rawPct = Number(r.percent ?? 0);
      const pct = normalizePercent(rawPct);
      if (!Number.isFinite(year) || !Number.isFinite(pct)) return;
      yearsSet.add(year);
      if (!caSeries[cat]) caSeries[cat] = {};
      caSeries[cat][year] = pct;
    });

    const californiaPercentAny: Record<number, number> = {};
    yearsArr.forEach(year => {
      let total = 0;
      Object.entries(caSeries).forEach(([cat, map]) => {
        const v = map[year];
        const lc = cat.toLowerCase();
        if (lc.startsWith('with') && Number.isFinite(v)) total += v;
      });
      californiaPercentAny[year] = total;
    });

    const lastYear = yearsArr[yearsArr.length - 1] ?? null;
    const lastPct = lastYear != null ? percentAny[lastYear] ?? null : null;
    const caLastPct = lastYear != null ? californiaPercentAny[lastYear] ?? null : null;

    return {
      years: yearsArr,
      seriesByCategory: series,
      percentWithAnyByYear: percentAny,
      caPercentWithAnyByYear: californiaPercentAny,
      latestYear: lastYear,
      latestPercentWithAny: lastPct,
      latestCaliforniaPercentWithAny: caLastPct
    };
  }, [rows]);

  useEffect(() => {
    if (activeChart !== 'snapshot') {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
      return;
    }
    if (!canvasRef.current || !years.length || !hasData) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {}
      chartRef.current = null;
    }

    const palette = [
      '#1f77b4',
      '#ff7f0e',
      '#2ca02c',
      '#d62728',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf'
    ];

    // Latest-year bar chart: With SNAP vs Without SNAP
    const latest = latestYear;
    if (latest == null) return;

    let withSnap = 0;
    let explicitWithoutSnap: number | null = null;

    Object.entries(seriesByCategory).forEach(([cat, map]) => {
      const v = map[latest as number];
      if (!Number.isFinite(v)) return;
      const lc = cat.toLowerCase();
      if (lc.startsWith('with')) {
        withSnap += v as number;
      } else if (lc.startsWith('no') || lc.startsWith('without')) {
        explicitWithoutSnap = (explicitWithoutSnap ?? 0) + (v as number);
      }
    });

    if (!Number.isFinite(withSnap) || withSnap <= 0) {
      setHasData(false);
      return;
    }

    const withoutSnap = explicitWithoutSnap != null ? explicitWithoutSnap : Math.max(0, 100 - withSnap);

    const labels = ['With SNAP', 'Without SNAP'];
    const dataValues = [withSnap, withoutSnap];

    const data = {
      labels,
      datasets: [
        {
          data: dataValues,
          backgroundColor: [palette[0], palette[1]],
          borderColor: [palette[0], palette[1]],
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: standardTitle(`Households With and Without SNAP (${latest}) - ${location}`),
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.label || '';
              const v = ctx.parsed?.y;
              if (v == null) return `${label}: N/A`;
              return `${label}: ${v.toFixed(1)}% of households`;
            }
          }
        }
      },
      scales: {
        x: {
          title: { display: false }
        },
        y: {
          beginAtZero: true,
          max: 100,
          title: { display: true, text: '% of Households' },
          ticks: {
            callback: (v: any) => `${v}%`
          }
        }
      }
    };

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data,
      options
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
    };
  }, [activeChart, years, seriesByCategory, percentWithAnyByYear, hasData, location]);

  useEffect(() => {
    if (activeChart !== 'comparison') {
      if (compareChartRef.current) {
        try {
          compareChartRef.current.destroy();
        } catch {}
        compareChartRef.current = null;
      }
      return;
    }
    if (!compareCanvasRef.current || !years.length || !hasData) return;
    const ctx = compareCanvasRef.current.getContext('2d');
    if (!ctx) return;

    if (compareChartRef.current) {
      try {
        compareChartRef.current.destroy();
      } catch {}
      compareChartRef.current = null;
    }

    compareChartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: [
          {
            label: `${location}`,
            data: years.map(y => (percentWithAnyByYear[y] != null ? percentWithAnyByYear[y] : null)),
            borderColor: '#1f77b4',
            backgroundColor: 'rgba(31, 119, 180, 0.08)',
            tension: 0.25,
            borderWidth: 2.5,
            pointRadius: 3
          },
          {
            label: 'California',
            data: years.map(y => (caPercentWithAnyByYear[y] != null ? caPercentWithAnyByYear[y] : null)),
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.08)',
            tension: 0.25,
            borderWidth: 2.5,
            pointRadius: 3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Comparison: Households With Cash Assistance/SNAP (${location} vs California)`),
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const label = ctx.dataset?.label || '';
                const v = ctx.parsed?.y;
                if (v == null) return `${label}: N/A`;
                return `${label}: ${v.toFixed(1)}%`;
              }
            }
          }
        },
        scales: {
          x: { title: { display: true, text: 'Year' } },
          y: {
            beginAtZero: true,
            max: 100,
            title: { display: true, text: '% of households with assistance/SNAP' },
            ticks: { callback: (v: any) => `${v}%` }
          }
        }
      }
    });

    return () => {
      if (compareChartRef.current) {
        try {
          compareChartRef.current.destroy();
        } catch {}
        compareChartRef.current = null;
      }
    };
  }, [activeChart, years, percentWithAnyByYear, caPercentWithAnyByYear, hasData, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Cash_Public_Assistance');
    downloadChartAsPNG(canvasRef, filename);
  };

  const handleDownloadCSV = () => {
    downloadDataAsCSV(rows, formatFilename(location, 'Cash_Public_Assistance_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading cash public assistance data for {location}…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 380, padding: 24, textAlign: 'center' }}>
        <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>Location: {location || 'Not set'}</div>
      </div>
    );
  }

  if (!hasData || !years.length) {
    return (
      <div style={{ height: 380, padding: 24, textAlign: 'center' }}>
        <div>No cash public assistance data available for {location}.</div>
      </div>
    );
  }

  const keyMetricsDisplay =
    latestYear != null && latestPercentWithAny != null ? (
      <KeyMetrics>
        <KeyMetric
          value={`${latestPercentWithAny.toFixed(1)}%`}
          label={`Households with cash assistance / SNAP (${latestYear})`}
          color="#1e3a8a"
        />
        {latestCaliforniaPercentWithAny != null && (
          <KeyMetric
            value={`${latestCaliforniaPercentWithAny.toFixed(1)}%`}
            label={`California households with cash assistance / SNAP (${latestYear})`}
            color="#047857"
          />
        )}
        {latestCaliforniaPercentWithAny != null && (
          <KeyMetric
            value={`${(latestPercentWithAny - latestCaliforniaPercentWithAny).toFixed(1)} pts`}
            label={`${location} vs California gap (${latestYear})`}
            color="#7c3aed"
          />
        )}
      </KeyMetrics>
    ) : null;

  const overviewContent = (
    <OverviewContent
      title="Overview"
      description={`This chart shows the share of households in ${location} receiving cash public assistance income or SNAP benefits over time.`}
    >
      <p>
        Tracking public assistance participation helps local governments understand economic stress, identify
        communities that may be more vulnerable to shocks, and plan safety net policies that align with climate and
        resilience goals.
      </p>
    </OverviewContent>
  );

  const relevanceContent = (
    <RelevanceContent
      title="Relevance"
      description="Households relying on cash assistance or SNAP often face higher energy and transportation burdens."
    >
      <div className="methodology-details-simple">
        <h5>Energy Burden</h5>
        <p>
          Lower-income households typically spend a larger share of their income on basic needs such as utility bills and
          transportation, which can limit their ability to invest in efficiency upgrades or clean energy.
        </p>
      </div>
      <div className="methodology-details-simple">
        <h5>Program Targeting</h5>
        <p>
          Understanding where public assistance participation is highest can guide targeting of bill assistance, weatherization,
          and electrification programs to households with the greatest financial constraints.
        </p>
      </div>
    </RelevanceContent>
  );

  const equityContent = (
    <EquityContent
      title="Equity"
      description="Public assistance data highlights where households may be most vulnerable to income shocks, rising costs, and climate impacts."
    >
      <div className="methodology-details-simple">
        <h5>Income Insecurity</h5>
        <p>
          Higher rates of assistance usage signal greater income insecurity, making it important to design climate and
          resilience programs that do not increase costs for these households.
        </p>
      </div>
      <div className="methodology-details-simple">
        <h5>Access to Benefits</h5>
        <p>
          Changes in participation over time can reflect both economic conditions and barriers to accessing benefits, such as
          administrative burden, language access, or immigration concerns.
        </p>
      </div>
    </EquityContent>
  );

  const methodologyContent = (
    <MethodologyContent
      title="Data Sources and Methodology"
      description="Data come from the American Community Survey (ACS) 5-year estimates on cash public assistance income and SNAP benefits."
      dataSources={[
        'US Census Bureau, American Community Survey (ACS) 5-Year Estimates – Cash Public Assistance Income',
        'US Census Bureau, American Community Survey (ACS) 5-Year Estimates – SNAP / Food Stamp Benefits'
      ]}
      methodology={[
        'Household counts are converted to percentages by dividing by total households in each geography and year.',
        'The chart shows time series for each category in the ACS table (e.g., with/without assistance).',
        'An approximate “any assistance” series sums categories that reference cash public assistance or SNAP benefits.',
        'Values represent share of households in the jurisdiction, not individuals.'
      ]}
    />
  );

  const chartContent =
    activeChart === 'snapshot' ? (
      <div style={{ height: 360, width: '100%', position: 'relative' }}>
        <canvas ref={canvasRef} style={{ height: '100%', width: '100%' }} />
      </div>
    ) : (
      <div style={{ height: 360, width: '100%', position: 'relative' }}>
        <canvas ref={compareCanvasRef} style={{ height: '100%', width: '100%' }} />
      </div>
    );

  return (
    <ChartLayout
      title="Households Receiving Cash Assistance & SNAP"
      key={`cash-public-assistance-${location}`}
      description={`${location} • Source: ACS 5-Year (Cash Public Assistance Income & SNAP)`}
      chartType="bar"
      chartSelector={{
        value: activeChart,
        onChange: (value: string) => setActiveChart(value as 'snapshot' | 'comparison'),
        options: [
          { value: 'snapshot', label: 'Latest Snapshot' },
          { value: 'comparison', label: 'vs California' }
        ]
      }}
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView="overview"
      onViewChange={() => {}}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Cash_Public_Assistance')}
      csvFilename={formatFilename(location, 'Cash_Public_Assistance_Data')}
      enableComments={true}
      location={location}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      equityContent={equityContent}
      methodologyContent={methodologyContent}
    />
  );
};

export default CashPublicAssistanceChart;

